import { computed, Injectable, signal } from '@angular/core'
import { kDefaultRoleId, kRoleAll, kRoleProfiles, kRoutesByNavLabel, ROLE_PROFILE_BY_ID, type AppRoleId, type AppRoleProfile, type AppRolePageKey } from '@core/role/app-role'
import { type NavLink } from '@core/type/link'
import { IS_SET } from '@core/util/check.util'
import { ENV_ACTV } from '@core/util/env.util'

/*
  holds the role currently played by the user and answers "is this feature part of it ?".
  The role is a UI simplification, not a security boundary : the backend keeps enforcing its
  own permissions. It is picked in the sidenav and remembered in localStorage.
*/
@Injectable({ providedIn: 'root' })
export class RoleService {
  // localStorage key holding the selected role id
  private readonly kStorageKey = 'gateway_role'

  // the role selector is a demo feature : ACTV builds always run the complete UI
  readonly bRoleUiEnabled = !ENV_ACTV

  // every selectable role, in menu order
  readonly roleProfiles: AppRoleProfile[] = kRoleProfiles

  // the active role id, restored from the previous session
  readonly currentRoleIdWS = signal<AppRoleId>(this.readStoredRoleId())

  // the active role profile, recomputed whenever the id changes
  readonly currentRoleWS = computed<AppRoleProfile>(() => ROLE_PROFILE_BY_ID(this.currentRoleIdWS()))

  // selects a role and remembers it for the next session
  setRole (inId: AppRoleId): void {
    this.currentRoleIdWS.set(inId)
    localStorage.setItem(this.kStorageKey, inId)
  }

  // whether a menu entry (NavLink label) belongs to the active role
  isNavLabelAllowed (inLabel: string): boolean {
    const vLabels = this.currentRoleWS().navLabels
    return vLabels.includes(kRoleAll) || vLabels.includes(inLabel)
  }

  // keeps the links allowed by the active role, or parents holding at least one allowed
  // child (their children are pruned to the allowed ones)
  filterNavLinks (inLinks: NavLink[]): NavLink[] {
    const vResult: NavLink[] = []

    for (const vLink of inLinks) {
      if (this.isNavLabelAllowed(vLink.label)) {
        vResult.push(vLink)
      } else if (IS_SET(vLink.children)) {
        const vChildren = vLink.children.filter(vChild => this.isNavLabelAllowed(vChild.label))
        if (vChildren.length > 0) {
          vResult.push({ ...vLink, children: vChildren })
        }
      }
    }

    return vResult
  }

  // whether a router URL may be opened by the active role. URLs owned by no menu entry
  // (login, 404, internal redirects) stay allowed.
  isPathAllowed (inUrl: string): boolean {
    const vUrl = inUrl.split('?')[0].split('#')[0]

    let vOwnerLabel = ''
    let vOwnerLength = 0

    for (const vLabel of Object.keys(kRoutesByNavLabel)) {
      for (const vRoute of kRoutesByNavLabel[vLabel]) {
        const bMatch = vUrl === vRoute || vUrl.startsWith(`${vRoute}/`)
        // the longest matching prefix owns the URL ('/supervision/wall' over '/supervision')
        if (bMatch && vRoute.length > vOwnerLength) {
          vOwnerLabel = vLabel
          vOwnerLength = vRoute.length
        }
      }
    }

    return vOwnerLabel === '' || this.isNavLabelAllowed(vOwnerLabel)
  }

  // whether a thematic tab of a page belongs to the active role ('all' is always kept,
  // it is the "no filter" tab of every thematic row)
  isThemeAllowed (inPage: AppRolePageKey, inKey: string): boolean {
    const vKeys = this.currentRoleWS().themeKeys[inPage]
    return inKey === 'all' || vKeys.includes(kRoleAll) || vKeys.includes(inKey)
  }

  // keeps the thematic tabs of a page that belong to the active role ; inKeyOf reads the
  // key of a tab, whose property name differs from one page to the next
  filterThemes<T> (inPage: AppRolePageKey, inThemes: T[], inKeyOf: (inTheme: T) => string): T[] {
    return inThemes.filter(vTheme => this.isThemeAllowed(inPage, inKeyOf(vTheme)))
  }

  // the role id stored by the previous session, defaulting to the complete UI
  private readStoredRoleId (): AppRoleId {
    // ACTV builds ignore any stored value : they always run the complete UI
    const vStored = ENV_ACTV ? '' : (localStorage.getItem(this.kStorageKey) ?? '')
    return kRoleProfiles.some(_ => _.id === vStored) ? (vStored as AppRoleId) : kDefaultRoleId
  }
}
